import React, { useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Equipment } from '@models/equipment.model';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { Checkbox } from '@components/atoms/Checkbox';
import { FormGroup } from '@components/molecules/form/FormGroup';
import {
  formatDateForSubmission,
  formatDateTimeForSubmission,
} from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';

interface CreateEditEquipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (equipment: any) => void;
  equipment?: Equipment | null;
  isDelegated?: boolean;
}

interface EquipmentFormValues {
  name: string;
  supplierOwner: string;
  time: string;
  hoursUsed: number;
  amount: number;
  costPerHour: number;
  pricePerHour: number;
  chargeToClient: boolean;
}

const buildValidationSchema = (t: Record<string, string>) =>
  Yup.object().shape({
    name: Yup.string().min(3, t.nameMinLength).required(t.nameRequired),
    supplierOwner: Yup.string().required(t.supplierOwnerRequired),
    time: Yup.string().required(t.supplyDeliveryDateRequired),
    hoursUsed: Yup.number()
      .min(0, t.hoursUsedPositive)
      .required(t.hoursUsedRequired),
    amount: Yup.number().min(0, t.amountPositive).required(t.amountRequired),
    costPerHour: Yup.number()
      .min(0, t.costPerHourPositive)
      .required(t.costPerHourRequired)
      .max(99999999, t.costPerHourPositive),
    pricePerHour: Yup.number().when('chargeToClient', {
      is: true,
      then: () =>
        Yup.number()
          .max(99999999, t.pricePerHourPositiveWhenCharging)
          .min(0, t.pricePerHourRequiredWhenCharging)
          .required(t.pricePerHourRequiredWhenCharging),
      otherwise: () => Yup.number().min(0, t.pricePerHourPositive),
    }),
    chargeToClient: Yup.boolean(),
  });

export const CreateEditEquipmentModal: React.FC<
  CreateEditEquipmentModalProps
> = ({ isOpen, onSave, onClose, equipment, isDelegated = false }) => {
  const t = useTranslation('equipmentModal');
  const validationSchema = useMemo(() => buildValidationSchema(t), [t]);

  const getInitialValues = (): EquipmentFormValues => ({
    name: equipment?.name || '',
    supplierOwner: equipment?.supplierOwner || '',
    time: formatDateForSubmission(equipment?.time || null) || '',
    hoursUsed: equipment?.hoursUsed || 0,
    amount: equipment?.amount || 0,
    costPerHour: equipment?.costPerHour || 0,
    pricePerHour: equipment?.chargeToClient ? equipment?.pricePerHour || 0 : 0,
    chargeToClient: equipment?.chargeToClient || false,
  });

  const handleSubmit = (values: EquipmentFormValues) => {
    const equipmentData = {
      ...values,
      equipmentId:
        equipment?.equipmentId || `temp-${Date.now()}-${Math.random()}`,
      time: formatDateTimeForSubmission(values.time || null),
      pricePerHour: values.chargeToClient ? values.pricePerHour : undefined,
    };

    onSave(equipmentData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize>
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleSubmit: formikSubmit,
      }) => (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          handleConfirm={formikSubmit}
          confirmText={equipment ? t.saveChanges : t.addEquipment}
          title={equipment ? t.editEquipment : t.addEquipment}>
          <Form className="space-y-4" onClick={(e) => e.stopPropagation()}>
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormGroup
                label={t.nameLabel}
                htmlFor="name"
                error={touched.name && errors.name ? errors.name : undefined}>
                <Input
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue('name', e.target.value)}
                  placeholder={t.equipmentNamePlaceholder}
                  autoComplete="off"
                  className={
                    touched.name && errors.name ? 'border-red-500' : ''
                  }
                />
              </FormGroup>

              <FormGroup
                label={t.supplierOwnerLabel}
                htmlFor="supplierOwner"
                error={
                  touched.supplierOwner && errors.supplierOwner
                    ? errors.supplierOwner
                    : undefined
                }>
                <Input
                  name="supplierOwner"
                  value={values.supplierOwner}
                  onChange={(e) =>
                    setFieldValue('supplierOwner', e.target.value)
                  }
                  placeholder={t.equipmentSupplierPlaceholder}
                  className={
                    touched.supplierOwner && errors.supplierOwner
                      ? 'border-red-500'
                      : ''
                  }
                />
              </FormGroup>
            </div>

            {/* Date */}
            <FormGroup
              label={t.supplyDeliveryDateLabel}
              htmlFor="time"
              error={touched.time && errors.time ? errors.time : undefined}>
              <Input
                type="date"
                name="time"
                value={values.time}
                onChange={(e) => setFieldValue('time', e.target.value)}
                className={`block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500 ${
                  touched.time && errors.time ? 'border-red-500' : ''
                }`}
              />
            </FormGroup>

            {/* Numeric Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormGroup
                label={t.hoursUsedLabel}
                htmlFor="hoursUsed"
                error={
                  touched.hoursUsed && errors.hoursUsed
                    ? errors.hoursUsed
                    : undefined
                }>
                <div className="flex items-center space-x-2">
                  <Input
                    name="hoursUsed"
                    type="number"
                    value={values.hoursUsed.toString()}
                    onChange={(e) =>
                      setFieldValue('hoursUsed', Number(e.target.value))
                    }
                    placeholder="120"
                    className={
                      touched.hoursUsed && errors.hoursUsed
                        ? 'border-red-500'
                        : ''
                    }
                  />
                </div>
              </FormGroup>

              <FormGroup
                label={t.amountLabel}
                htmlFor="amount"
                error={
                  touched.amount && errors.amount ? errors.amount : undefined
                }>
                <div className="flex items-center space-x-2">
                  <Input
                    name="amount"
                    type="number"
                    value={values.amount.toString()}
                    onChange={(e) =>
                      setFieldValue('amount', Number(e.target.value))
                    }
                    placeholder="120"
                    className={
                      touched.amount && errors.amount ? 'border-red-500' : ''
                    }
                  />
                </div>
              </FormGroup>

              <FormGroup
                label={t.costPerHourLabel}
                htmlFor="costPerHour"
                error={
                  touched.costPerHour && errors.costPerHour
                    ? errors.costPerHour
                    : undefined
                }>
                <InputCurrency
                  name="costPerHour"
                  value={values.costPerHour}
                  onChange={(value) => setFieldValue('costPerHour', value || 0)}
                  placeholder="0.00"
                  error={touched.costPerHour && !!errors.costPerHour}
                />
              </FormGroup>
            </div>

            {/* Price per Hour (conditional) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Charge to Client Checkbox */}
              {isDelegated && (
                <FormGroup label="" htmlFor="chargeToClient">
                  <Checkbox
                    name="chargeToClient"
                    checked={values.chargeToClient}
                    onChange={(checked) =>
                      setFieldValue('chargeToClient', checked)
                    }
                    label={t.chargeToClient}
                  />
                </FormGroup>
              )}
              {values.chargeToClient && (
                <FormGroup
                  label={t.pricePerHourLabel}
                  htmlFor="pricePerHour"
                  error={
                    touched.pricePerHour && errors.pricePerHour
                      ? errors.pricePerHour
                      : undefined
                  }>
                  <InputCurrency
                    name="pricePerHour"
                    value={values.pricePerHour}
                    onChange={(value) =>
                      setFieldValue('pricePerHour', value || 0)
                    }
                    placeholder="0.00"
                    error={touched.pricePerHour && !!errors.pricePerHour}
                  />
                  <div className="flex items-start space-x-2">
                    {values?.pricePerHour > 0 &&
                      values?.costPerHour > 0 &&
                      values?.amount > 0 && (
                        <div
                          className={`mt-1 text-sm ${
                            values.pricePerHour < values.costPerHour
                              ? 'text-yellow-500'
                              : 'text-green-600'
                          }`}>
                          {t.rawProfit.replace(
                            '{amount}',
                            `$${(
                              values.amount *
                              (values.pricePerHour - values.costPerHour)
                            ).toFixed(2)}`,
                          )}
                        </div>
                      )}
                  </div>
                </FormGroup>
              )}
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
